import {
  Alert,
  Box,
  Button,
  Card,
  Heading,
  Icon,
  Modal,
  Paragraph,
  Popover,
  Spinner,
  Text,
} from '@zillow/constellation-10';
import { IconInfoFilled } from '@zillow/constellation-icons';
import { usePropertyDailyBreakdown } from './usePropertyDailyBreakdown';
import { PropertyDetailGraph } from './Graphs/PropertyDetailGraph';
import { PropertyDetailTable } from './PropertyDetailTable';
import { formatNumber } from '../../../../utils';
import { TableType } from './AdSpendReport.config';

type PropertyDetailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  listingAlias: string;
  propertyName: string;
  startDate: string;
  endDate: string;
  tableType: TableType;
};

type MetricCardProps = {
  title: string;
  infoText: string;
  value: number;
};

const MetricCard = ({ title, infoText, value }: MetricCardProps) => (
  <Card css={{ padding: '400' }} elevated={false}>
    <Popover
      header={null}
      closeButton={<span hidden />}
      body={<Paragraph textStyle="body-sm">{infoText}</Paragraph>}
      trigger={
        <Box
          css={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: '100',
            cursor: 'pointer',
          }}
        >
          <Text textStyle="body-sm-bold" asChild css={{ whiteSpace: 'nowrap' }}>
            <h4>{title}</h4>
          </Text>
          <Icon size="lg" css={{ color: 'gray.500', paddingRight: '13px' }}>
            <IconInfoFilled />
          </Icon>
        </Box>
      }
      shouldAwaitInteractionResponse
      tone="neutral"
    />
    <Heading level={5}>{formatNumber(value)}</Heading>
  </Card>
);

export const PropertyDetailModal = ({
  isOpen,
  onClose,
  listingAlias,
  propertyName,
  startDate,
  endDate,
  tableType,
}: PropertyDetailModalProps) => {
  const { value: data, loading, error } = usePropertyDailyBreakdown(isOpen ? listingAlias : null, startDate, endDate);

  const renderContent = () => {
    if (loading) {
      return (
        <Box css={{ display: 'flex', justifyContent: 'center', padding: '800' }}>
          <Spinner size="lg" />
        </Box>
      );
    }

    if (error) {
      return <Alert tone="critical">Failed to load property details. Please try again later.</Alert>;
    }

    if (!data || !data.dailyBreakdowns || data.dailyBreakdowns.length === 0) {
      return (
        <Box css={{ padding: '800', textAlign: 'center' }}>
          <Paragraph>No performance data available for this property in the selected period.</Paragraph>
        </Box>
      );
    }

    const isExtend = tableType === TableType.EXTEND;

    if (isExtend) {
      const totalAddOnImpressions = data.dailyBreakdowns.reduce((sum, b) => sum + (b.addOnImpressions ?? 0), 0);
      const totalAddOnClicks = data.dailyBreakdowns.reduce((sum, b) => sum + (b.addOnClickCount ?? 0), 0);
      const totalMetaImpressions = data.dailyBreakdowns.reduce((sum, b) => sum + (b.addOnMetaImpressions ?? 0), 0);
      const totalMetaClicks = data.dailyBreakdowns.reduce((sum, b) => sum + (b.addOnMetaClicks ?? 0), 0);
      const totalTiktokImpressions = data.dailyBreakdowns.reduce((sum, b) => sum + (b.addOnTiktokImpressions ?? 0), 0);
      const totalTiktokClicks = data.dailyBreakdowns.reduce((sum, b) => sum + (b.addOnTiktokClicks ?? 0), 0);

      return (
        <Box>
          <Box css={{ marginBottom: '600' }}>
            <Heading level={4}>Extend performance</Heading>
            <Paragraph css={{ color: 'text.subtle' }}>
              Your listing&apos;s daily Extend impressions and ad clicks by channel.
            </Paragraph>
          </Box>

          <Text css={{ fontWeight: 'bold', marginBottom: '400', display: 'block' }}>Performance trends</Text>
          <Box css={{ display: 'flex', gap: '400', marginBottom: '600', flexWrap: 'wrap' }}>
            <MetricCard
              title="Total Impressions"
              infoText="Sum of daily Extend impressions for the selected period."
              value={totalAddOnImpressions}
            />
            <MetricCard
              title="Total Clicks"
              infoText="Sum of daily Extend ad clicks for the selected period."
              value={totalAddOnClicks}
            />
            <MetricCard
              title="Meta Impressions"
              infoText="Sum of daily Meta impressions for the selected period."
              value={totalMetaImpressions}
            />
            <MetricCard
              title="Meta Clicks"
              infoText="Sum of daily Meta ad clicks for the selected period."
              value={totalMetaClicks}
            />
            <MetricCard
              title="TikTok Impressions"
              infoText="Sum of daily TikTok impressions for the selected period."
              value={totalTiktokImpressions}
            />
            <MetricCard
              title="TikTok Clicks"
              infoText="Sum of daily TikTok ad clicks for the selected period."
              value={totalTiktokClicks}
            />
          </Box>

          <PropertyDetailGraph dailyBreakdowns={data.dailyBreakdowns} isExtend />

          <PropertyDetailTable dailyBreakdowns={data.dailyBreakdowns} tableType={tableType} />
        </Box>
      );
    }

    const isBoost = tableType === TableType.BOOST;

    if (isBoost) {
      const totalBoostImpressions = data.dailyBreakdowns.reduce((sum, b) => sum + (b.regularBoostImpressions ?? 0), 0);
      const totalBoostClicks = data.dailyBreakdowns.reduce((sum, b) => sum + (b.regularBoostClicks ?? 0), 0);

      return (
        <Box>
          <Box css={{ marginBottom: '600' }}>
            <Heading level={4}>Boost performance</Heading>
            <Paragraph css={{ color: 'text.subtle' }}>
              Your listing&apos;s daily Boost impressions and ad clicks.
            </Paragraph>
          </Box>

          <Text css={{ fontWeight: 'bold', marginBottom: '400', display: 'block' }}>Performance trends</Text>
          <Box css={{ display: 'flex', gap: '400', marginBottom: '600', flexWrap: 'wrap' }}>
            <MetricCard
              title="Impressions"
              infoText="Sum of daily Boost impressions for the selected period."
              value={totalBoostImpressions}
            />
            <MetricCard
              title="Ad Clicks"
              infoText="Sum of daily Boost ad clicks for the selected period."
              value={totalBoostClicks}
            />
          </Box>

          <PropertyDetailGraph dailyBreakdowns={data.dailyBreakdowns} isBoost />

          <PropertyDetailTable dailyBreakdowns={data.dailyBreakdowns} tableType={tableType} />
        </Box>
      );
    }

    const portfolioImpressions = data.insights?.portfolioImpressionCount ?? 0;
    const portfolioViews = data.insights?.portfolioViewCount ?? 0;
    const portfolioLeads = data.insights?.portfolioLeadsTotal ?? 0;
    const portfolioClickCount = data.insights?.portfolioClickCount ?? 0;
    const portfolioListingWebReferrals = data.insights?.portfolioListingWebReferrals ?? 0;
    const portfolioAdClicks = portfolioClickCount - portfolioListingWebReferrals;

    return (
      <Box>
        <Box css={{ marginBottom: '600' }}>
          <Heading level={4}>Listing performance</Heading>
          <Paragraph css={{ color: 'text.subtle' }}>
            Your listing&apos;s daily impressions, views, leads, listing web referrals, and ad clicks.
          </Paragraph>
        </Box>

        <Text css={{ fontWeight: 'bold', marginBottom: '400', display: 'block' }}>Performance trends</Text>
        <Box css={{ display: 'flex', gap: '400', marginBottom: '600', flexWrap: 'wrap' }}>
          <MetricCard
            title="Impressions"
            infoText="Sum of daily impressions across your portfolio for the selected period."
            value={portfolioImpressions}
          />
          <MetricCard
            title="Views"
            infoText="Sum of daily views across your portfolio for the selected period."
            value={portfolioViews}
          />
          <MetricCard
            title="Listing Web Referrals"
            infoText="Sum of daily listing web referrals across your portfolio for the selected period."
            value={portfolioListingWebReferrals}
          />
          <MetricCard
            title="Ad Clicks"
            infoText="Sum of daily ad clicks across your portfolio for the selected period."
            value={portfolioAdClicks}
          />
          <MetricCard
            title="Leads"
            infoText="Sum of daily leads across your portfolio for the selected period."
            value={portfolioLeads}
          />
        </Box>

        <PropertyDetailGraph dailyBreakdowns={data.dailyBreakdowns} />

        <PropertyDetailTable dailyBreakdowns={data.dailyBreakdowns} tableType={tableType} />
      </Box>
    );
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="xl"
      header={propertyName || 'Property Details'}
      body={renderContent()}
      footer={
        <Box css={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button tone="brand" emphasis="filled" onClick={onClose}>
            Close
          </Button>
        </Box>
      }
    />
  );
};
